/**
 * Serialization helpers for BuildExplorer to support worker thread compilation.
 *
 * The graph compilation process produces objects with methods (Relation, Expression, etc.)
 * that can't be transferred between threads. This module provides serialization/deserialization
 * functions that convert these objects to/from plain data using their AST representations.
 */

import { Expression, Relation, AST, Ty } from '@cotera/era';
import { ok, err } from 'neverthrow';
import { Graph } from '../graph';
import { DocumentEditor } from '../document';
import type { EventStreamDefinition } from '../../manifest';
import type { Commit } from './commit';
import type { IdentityMap } from './identity-map';

// ============================================================================
// Serialized Type Definitions
// ============================================================================

export type SerializedIdentity = [commitIndex: number, opIndex: number];

export type SerializedExpression = {
  readonly _t: 'expression';
  readonly ast: AST.ExprFR;
};

export type SerializedRelation = {
  readonly _t: 'relation';
  readonly ast: AST.RelFR;
};

export type SerializedColMetadata =
  | { readonly t: 'eraql'; readonly expr: SerializedExpression }
  | { readonly t: 'llm'; readonly prompt: SerializedExpression }
  | { readonly t: 'workflow' };

export type SerializedNodeMeta = {
  readonly identity: SerializedIdentity;
  readonly isLocked: boolean;
  readonly editsAt: readonly SerializedIdentity[];
  readonly predecessor?: SerializedIdentity;
};

export type SerializedExprNode = {
  readonly t: 'expr';
  readonly source: string;
  readonly meta: SerializedNodeMeta;
};

export type SerializedDocumentNode = {
  readonly t: 'document';
  readonly source: DocumentEditor.DocumentState;
  readonly meta: SerializedNodeMeta;
};

export type SerializedRelNode = {
  readonly t: 'rel';
  readonly source: string;
  readonly pk: string | null;
  readonly where: string | null;
  readonly cols: {
    readonly [name: string]: {
      readonly def: Graph.ColConfig;
      readonly meta: SerializedNodeMeta;
    };
  };
  readonly meta: SerializedNodeMeta;
};

export type SerializedToolNode = {
  readonly t: 'tool';
  readonly def: Graph.ToolItemDef;
  readonly meta: SerializedNodeMeta;
};

export type SerializedAgentNode = {
  readonly t: 'agent';
  readonly outputTy: string;
  readonly editorState: DocumentEditor.DocumentState;
  readonly model: string;
  readonly schedule?: 'never' | 'daily' | 'hourly' | { cron: string };
  readonly meta: SerializedNodeMeta;
};

export type SerializedGraphNode =
  | SerializedExprNode
  | SerializedDocumentNode
  | SerializedRelNode
  | SerializedToolNode
  | SerializedAgentNode;

export type SerializedBuiltColNode = {
  readonly identity: SerializedIdentity;
  readonly meta: SerializedColMetadata;
  readonly eventStream: EventStreamDefinition | null;
};

export type SerializedRelItem = {
  readonly t: 'rel';
  readonly identity: SerializedIdentity;
  readonly rel: SerializedRelation;
  readonly pk: SerializedExpression | null;
  readonly where: SerializedExpression | null;
  readonly relNode: SerializedRelNode;
  readonly cols: { readonly [name: string]: SerializedBuiltColNode };
};

export type SerializedExprItem = {
  readonly t: 'expr';
  readonly exprNode: SerializedExprNode;
  readonly expr: SerializedExpression;
};

export type SerializedDocumentItem = {
  readonly t: 'document';
  readonly documentNode: SerializedDocumentNode;
};

export type SerializedToolItem = {
  readonly t: 'tool';
  readonly def: Graph.ToolItemDef;
};

export type SerializedAgentItem = {
  readonly t: 'agent';
  readonly agentNode: SerializedAgentNode;
  readonly availableToolSymbols: string[];
  readonly editorState: DocumentEditor.DocumentState;
  readonly output: Ty.ExtendedAttributeType;
  readonly model: string;
  readonly schedule?: 'never' | 'daily' | 'hourly' | { cron: string };
};

export type SerializedItem =
  | SerializedRelItem
  | SerializedExprItem
  | SerializedDocumentItem
  | SerializedToolItem
  | SerializedAgentItem;

export type SerializedResolution = {
  readonly item: SerializedItem;
  readonly deps: Graph.GraphDeps;
};

export type SerializedGraphErrorResult = {
  readonly err: Graph.GraphError;
  readonly partial: Graph.PartialSuccess | null;
};

export type SerializedGraphResult =
  | { readonly _ok: true; readonly value: SerializedResolution }
  | { readonly _ok: false; readonly error: SerializedGraphErrorResult };

export type SerializedBuildExplorerData = {
  readonly symbolMap: Record<string, SerializedIdentity>;
  readonly identitiesMap: {
    readonly [identString: string]: SerializedGraphNode;
  };
  readonly commits: readonly Commit[];
  readonly graph: Record<string, SerializedGraphResult>;
};

// ============================================================================
// Serialization Functions
// ============================================================================

export const serializeIdentity = (
  identity: Graph.Identity
): SerializedIdentity => {
  return identity.asTuple();
};

const serializeNodeMeta = (meta: Graph.NodeMeta): SerializedNodeMeta => {
  return {
    identity: serializeIdentity(meta.identity),
    isLocked: meta.isLocked,
    editsAt: meta.editsAt.map(serializeIdentity),
    ...(meta.predecessor !== undefined
      ? { predecessor: serializeIdentity(meta.predecessor) }
      : {}),
  };
};

const serializeGraphNode = (node: Graph.GraphNode): SerializedGraphNode => {
  switch (node.t) {
    case 'expr':
      return {
        t: 'expr',
        source: node.source,
        meta: serializeNodeMeta(node.meta),
      };
    case 'document':
      return {
        t: 'document',
        source: node.source,
        meta: serializeNodeMeta(node.meta),
      };
    case 'rel':
      return {
        t: 'rel',
        source: node.source,
        pk: node.pk,
        where: node.where,
        cols: Object.fromEntries(
          Object.entries(node.cols).map(([name, col]) => [
            name,
            {
              def: col.def,
              meta: serializeNodeMeta(col.meta),
            },
          ])
        ),
        meta: serializeNodeMeta(node.meta),
      };
    case 'tool':
      return {
        t: 'tool',
        def: node.def,
        meta: serializeNodeMeta(node.meta),
      };
    case 'agent':
      return {
        t: 'agent',
        outputTy: node.outputTy,
        editorState: node.editorState,
        model: node.model,
        schedule: node.schedule,
        meta: serializeNodeMeta(node.meta),
      };
  }
};

export const serializeExpression = (expr: Expression): SerializedExpression => {
  return { _t: 'expression', ast: expr.ast };
};

export const serializeRelation = (rel: Relation): SerializedRelation => {
  return { _t: 'relation', ast: rel.ast };
};

const serializeColMetadata = (
  meta: Graph.ColMetadata
): SerializedColMetadata => {
  switch (meta.t) {
    case 'eraql':
      return { t: 'eraql', expr: serializeExpression(meta.expr) };
    case 'llm':
      return { t: 'llm', prompt: serializeExpression(meta.prompt) };
    case 'workflow':
      return { t: 'workflow' };
  }
};

const serializeBuiltColNode = (
  col: Graph.BuiltColNode
): SerializedBuiltColNode => {
  return {
    identity: serializeIdentity(col.identity),
    meta: serializeColMetadata(col.meta),
    eventStream: col.eventStream,
  };
};

const serializeItem = (item: Graph.Item): SerializedItem => {
  switch (item.t) {
    case 'rel':
      return {
        t: 'rel',
        identity: serializeIdentity(item.identity),
        rel: serializeRelation(item.rel),
        pk: item.pk !== null ? serializeExpression(item.pk) : null,
        where: item.where !== null ? serializeExpression(item.where) : null,
        relNode: serializeGraphNode(item.relNode) as SerializedRelNode,
        cols: Object.fromEntries(
          Object.entries(item.cols).map(([name, col]) => [
            name,
            serializeBuiltColNode(col),
          ])
        ),
      };
    case 'expr':
      return {
        t: 'expr',
        exprNode: serializeGraphNode(item.exprNode) as SerializedExprNode,
        expr: serializeExpression(item.expr),
      };
    case 'document':
      return {
        t: 'document',
        documentNode: serializeGraphNode(
          item.documentNode
        ) as SerializedDocumentNode,
      };
    case 'tool':
      return {
        t: 'tool',
        def: item.def,
      };
    case 'agent':
      return {
        t: 'agent',
        agentNode: serializeGraphNode(item.agentNode) as SerializedAgentNode,
        availableToolSymbols: item.availableToolSymbols,
        editorState: item.editorState,
        output: item.output,
        model: item.model,
        schedule: item.schedule,
      };
  }
};

const serializeResolution = (
  resolution: Graph.Resolution
): SerializedResolution => {
  return {
    item: serializeItem(resolution.item),
    deps: resolution.deps,
  };
};

const serializeGraphResult = (
  result: Graph.GraphResult
): SerializedGraphResult => {
  if (result.isOk()) {
    return { _ok: true, value: serializeResolution(result.value) };
  } else {
    return {
      _ok: false,
      error: {
        err: result.error.err,
        partial: result.error.partial,
      },
    };
  }
};

export const serializeBuildExplorerData = (params: {
  symbolMap: Record<string, Graph.Identity>;
  identities: IdentityMap;
  commits: readonly Commit[];
  graph: Record<string, Graph.GraphResult>;
}): SerializedBuildExplorerData => {
  return {
    symbolMap: Object.fromEntries(
      Object.entries(params.symbolMap).map(([k, v]) => [
        k,
        serializeIdentity(v),
      ])
    ),
    identitiesMap: Object.fromEntries(
      Object.entries(params.identities.rawMap).map(([k, v]) => [
        k,
        serializeGraphNode(v),
      ])
    ),
    commits: params.commits,
    graph: Object.fromEntries(
      Object.entries(params.graph).map(([k, v]) => [k, serializeGraphResult(v)])
    ),
  };
};

// ============================================================================
// Deserialization Functions
// ============================================================================

export const deserializeIdentity = (
  data: SerializedIdentity
): Graph.Identity => {
  return Graph.Identity.fromTuple(data);
};

const deserializeNodeMeta = (data: SerializedNodeMeta): Graph.NodeMeta => {
  return {
    identity: deserializeIdentity(data.identity),
    isLocked: data.isLocked,
    editsAt: data.editsAt.map(deserializeIdentity),
    ...(data.predecessor !== undefined
      ? { predecessor: deserializeIdentity(data.predecessor) }
      : {}),
  };
};

const deserializeGraphNode = (node: SerializedGraphNode): Graph.GraphNode => {
  switch (node.t) {
    case 'expr':
      return {
        t: 'expr',
        source: node.source,
        meta: deserializeNodeMeta(node.meta),
      };
    case 'document':
      return {
        t: 'document',
        source: node.source,
        meta: deserializeNodeMeta(node.meta),
      };
    case 'rel':
      return {
        t: 'rel',
        source: node.source,
        pk: node.pk,
        where: node.where,
        cols: Object.fromEntries(
          Object.entries(node.cols).map(([name, col]) => [
            name,
            {
              def: col.def,
              meta: deserializeNodeMeta(col.meta),
            },
          ])
        ),
        meta: deserializeNodeMeta(node.meta),
      };
    case 'tool':
      return {
        t: 'tool',
        def: node.def,
        meta: deserializeNodeMeta(node.meta),
      };
    case 'agent':
      return {
        t: 'agent',
        outputTy: node.outputTy,
        editorState: node.editorState,
        model: node.model,
        schedule: node.schedule,
        meta: deserializeNodeMeta(node.meta),
      };
  }
};

export const deserializeExpression = (
  data: SerializedExpression
): Expression => {
  return Expression.fromAst(data.ast);
};

export const deserializeRelation = (data: SerializedRelation): Relation => {
  return Relation.fromAst(data.ast);
};

const deserializeColMetadata = (
  data: SerializedColMetadata
): Graph.ColMetadata => {
  switch (data.t) {
    case 'eraql':
      return { t: 'eraql', expr: deserializeExpression(data.expr) };
    case 'llm':
      return { t: 'llm', prompt: deserializeExpression(data.prompt) };
    case 'workflow':
      return { t: 'workflow' };
  }
};

const deserializeBuiltColNode = (
  data: SerializedBuiltColNode
): Graph.BuiltColNode => {
  return {
    identity: deserializeIdentity(data.identity),
    meta: deserializeColMetadata(data.meta),
    eventStream: data.eventStream,
  };
};

const deserializeItem = (item: SerializedItem): Graph.Item => {
  switch (item.t) {
    case 'rel':
      return {
        t: 'rel',
        identity: deserializeIdentity(item.identity),
        rel: deserializeRelation(item.rel),
        pk: item.pk !== null ? deserializeExpression(item.pk) : null,
        where: item.where !== null ? deserializeExpression(item.where) : null,
        relNode: deserializeGraphNode(item.relNode) as Graph.RelNode,
        cols: Object.fromEntries(
          Object.entries(item.cols).map(([name, col]) => [
            name,
            deserializeBuiltColNode(col),
          ])
        ),
      };
    case 'expr':
      return {
        t: 'expr',
        exprNode: deserializeGraphNode(item.exprNode) as Graph.ExprNode,
        expr: deserializeExpression(item.expr),
      };
    case 'document':
      return {
        t: 'document',
        documentNode: deserializeGraphNode(
          item.documentNode
        ) as Graph.DocumentNode,
      };
    case 'tool':
      return {
        t: 'tool',
        def: item.def,
      };
    case 'agent':
      return {
        t: 'agent',
        agentNode: deserializeGraphNode(item.agentNode) as Graph.AgentNode,
        availableToolSymbols: item.availableToolSymbols,
        editorState: item.editorState,
        output: item.output,
        model: item.model,
        schedule: item.schedule,
      };
  }
};

const deserializeResolution = (
  data: SerializedResolution
): Graph.Resolution => {
  return {
    item: deserializeItem(data.item),
    deps: data.deps,
  };
};

const deserializeGraphResult = (
  data: SerializedGraphResult
): Graph.GraphResult => {
  if (data._ok) {
    return ok(deserializeResolution(data.value));
  } else {
    return err(new Graph.GraphErrorResult(data.error.err, data.error.partial));
  }
};

export type DeserializedBuildExplorerData = {
  symbolMap: Record<string, Graph.Identity>;
  identitiesMap: { readonly [identString: string]: Graph.GraphNode };
  commits: readonly Commit[];
  graph: Record<string, Graph.GraphResult>;
};

export const deserializeBuildExplorerData = (
  data: SerializedBuildExplorerData
): DeserializedBuildExplorerData => {
  return {
    symbolMap: Object.fromEntries(
      Object.entries(data.symbolMap).map(([k, v]) => [
        k,
        deserializeIdentity(v),
      ])
    ),
    identitiesMap: Object.fromEntries(
      Object.entries(data.identitiesMap).map(([k, v]) => [
        k,
        deserializeGraphNode(v),
      ])
    ),
    commits: data.commits,
    graph: Object.fromEntries(
      Object.entries(data.graph).map(([k, v]) => [k, deserializeGraphResult(v)])
    ),
  };
};
